import React, { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './FlightRangeList.scss';
import { useAuth } from 'react-oidc-context';
import { useUserRole } from '../../hooks/useUserRole';
import IFlightRangeData from '../../types/FlightRanges';
import FlightRangeService from '../../services/FlightRangeService';
import '@aurodesignsystem/auro-button';

const FlightRangeList: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useUserRole();
  const [flightRanges, setFlightRanges] = useState<Array<IFlightRangeData>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  // Check if we're in the admin view
  const isAdminView = location.pathname === '/flight-ranges/admin';

  const [viewport, setViewport] = useState({
    width: window.innerWidth,
    isMobile: window.innerWidth < 768,
    isTablet: window.innerWidth >= 768 && window.innerWidth < 1024,
    isDesktop: window.innerWidth >= 1024
  });

  // Initialize sortConfig with no default sorting to maintain API order
  const [sortConfig, setSortConfig] = useState<{
    key: keyof IFlightRangeData | null;
    direction: 'asc' | 'desc' | null;
  }>({
    key: null,
    direction: null
  });

  React.useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setViewport({
        width,
        isMobile: width < 768,
        isTablet: width >= 768 && width < 1024,
        isDesktop: width >= 1024
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  React.useEffect(() => {
    const abortController = new AbortController();
    let isMounted = true;

    const fetchFlightRanges = async () => {
      try {
        if (isMounted) {
          setIsLoading(true);
          setError(null);
        }

        const token = auth.user?.access_token;
        const response = await FlightRangeService.getFlightRanges(token);

        // Only update state if component is still mounted
        if (isMounted && !abortController.signal.aborted) {
          setFlightRanges(response.data);
        }
      } catch (e) {
        // Don't show error if the request was aborted due to component unmounting
        if (e instanceof Error && e.name === 'AbortError') {
          console.log('Flight ranges request was aborted');
          return;
        }

        console.error('Error fetching flight ranges:', e);
        if (isMounted && !abortController.signal.aborted) {
          setError('Failed to load flight ranges');
        }
      } finally {
        if (isMounted && !abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchFlightRanges();

    // Cleanup function
    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [auth]);

  // Get eligible rows (those without discontinued dates) - only needed for admin users
  const eligibleRows = React.useMemo(() => {
    if (!isAdmin || !isAdminView) return [];
    return flightRanges.filter(row => !row.discontinueDate || row.discontinueDate.trim() === '');
  }, [flightRanges, isAdmin, isAdminView]);

  const eligibleRowIds = React.useMemo(() => {
    return new Set(eligibleRows.map(row => row.id));
  }, [eligibleRows]);

  // Check if all eligible rows are selected - only relevant for admin users
  const allRowsSelected = isAdmin && isAdminView &&
    eligibleRowIds.size > 0 &&
    Array.from(eligibleRowIds).every(id => selectedRows.has(id));

  // Check if some (but not all) eligible rows are selected - only relevant for admin users
  const hasSomeRowsSelected = isAdmin && isAdminView &&
    selectedRows.size > 0 &&
    selectedRows.size < eligibleRowIds.size &&
    Array.from(selectedRows).some(id => eligibleRowIds.has(id));

  const handleMasterCheckboxChange = useCallback(() => {
    if (!isAdmin || !isAdminView) return;

    if (allRowsSelected) {
      // Deselect all eligible rows
      setSelectedRows(prev => {
        const newSet = new Set(prev);
        eligibleRowIds.forEach(id => newSet.delete(id));
        return newSet;
      });
    } else {
      // Select all eligible rows
      setSelectedRows(prev => {
        const newSet = new Set(prev);
        eligibleRowIds.forEach(id => newSet.add(id));
        return newSet;
      });
    }
  }, [isAdmin, isAdminView, allRowsSelected, eligibleRowIds]);

  const handleRowCheckboxChange = useCallback((rowId: string) => {
    if (!isAdmin || !isAdminView) return;

    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  }, [isAdmin, isAdminView]);

  const handleContinue = useCallback((event?: Event) => {
    if (selectedRows.size === 0) return;

    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const selectedFlightRanges = flightRanges.filter(range => selectedRows.has(range.id));

    navigate('/flight-ranges/modify', {
      state: {
        selectedFlightRanges,
        selectedIds: Array.from(selectedRows)
      }
    });
  }, [selectedRows, flightRanges, navigate]);

  const handleSort = (key: keyof IFlightRangeData) => {
    let direction: 'asc' | 'desc' | null = 'asc';
    
    if (sortConfig.key === key) {
      if (sortConfig.direction === 'asc') {
        direction = 'desc';
      } else if (sortConfig.direction === 'desc') {
        direction = null;
      }
    }
    
    setSortConfig({ key, direction });
  };

  const sortedData = React.useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return flightRanges;

    return [...flightRanges].sort((a, b) => {
      const aValue = a[sortConfig.key!];
      const bValue = b[sortConfig.key!];

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aStr = String(aValue || '');
      const bStr = String(bValue || '');

      if (aStr < bStr) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aStr > bStr) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [flightRanges, sortConfig]);

  const renderSortIcon = (columnKey: keyof IFlightRangeData) => {
    if (sortConfig.key !== columnKey || !sortConfig.direction) {
      return null;
    }

    return (
      <div className="sort-icon active">
        {sortConfig.direction === 'asc' ? (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 4L12 8H4L8 4Z" fill="#0074CA"/>
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 12L4 8H12L8 12Z" fill="#0074CA"/>
          </svg>
        )}
      </div>
    );
  };

  const formatFlightRange = (start: number, end: number): string => {
    return `${start} - ${end}`;
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    return dateString;
  };

  // Calculate column span based on whether checkboxes are shown
  const tableColSpan = (isAdmin && isAdminView) ? 8 : 7;

  if (isLoading) {
    return (
      <div className="flight-ranges-table">
        <div className="flight-ranges-table__container">
          <div className="flight-ranges-table__loading">
            Loading flight ranges...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flight-ranges-table">
        <div className="flight-ranges-table__container">
          <div className="flight-ranges-table__error">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flight-ranges-table">
      <div className="flight-ranges-table__container">
        <div className="flight-ranges-table__content">
          <div className="flight-ranges-table__table-wrapper"
               role="region"
               aria-label="Flight ranges table"
               tabIndex={0}>
            <table className="flight-ranges-table__table" aria-label="Flight ranges">
              <thead>
                <tr>
                  {isAdmin && isAdminView && (
                    <th className="flight-ranges-table__checkbox-column" scope="col">
                      <input
                        type="checkbox"
                        className="flight-ranges-table__checkbox flight-ranges-table__master-checkbox"
                        checked={allRowsSelected}
                        ref={checkbox => {
                          if (checkbox) checkbox.indeterminate = hasSomeRowsSelected;
                        }}
                        onChange={handleMasterCheckboxChange}
                        aria-label="Select all eligible flight ranges"
                        disabled={eligibleRowIds.size === 0}
                      />
                    </th>
                  )}
                  <th scope="col">
                    <button
                      className="sort-button"
                      onClick={() => handleSort('marketingCarrier')}
                      aria-label="Sort by Marketing Carrier"
                    >
                      MKT. CARRIER
                      {renderSortIcon('marketingCarrier')}
                    </button>
                  </th>
                  <th scope="col">
                    <button
                      className="sort-button"
                      onClick={() => handleSort('start')}
                      aria-label="Sort by Flight Range"
                    >
                      FLIGHT RANGE
                      {renderSortIcon('start')}
                    </button>
                  </th>
                  <th scope="col">
                    <button
                      className="sort-button"
                      onClick={() => handleSort('operatingCarrier')}
                      aria-label="Sort by Operating Carrier"
                    >
                      OP. CARRIER
                      {renderSortIcon('operatingCarrier')}
                    </button>
                  </th>
                  <th scope="col" className="flight-ranges-table__hide-on-mobile flight-ranges-table__flight-type-column">
                    <button
                      className="sort-button"
                      onClick={() => handleSort('flightTypeDescription')}
                      aria-label="Sort by Flight Type"
                    >
                      FLIGHT TYPE
                      {renderSortIcon('flightTypeDescription')}
                    </button>
                  </th>
                  <th scope="col">
                    <button
                      className="sort-button"
                      onClick={() => handleSort('effectiveDate')}
                      aria-label="Sort by Effective Date"
                    >
                      EFFECTIVE
                      {renderSortIcon('effectiveDate')}
                    </button>
                  </th>
                  <th scope="col" className="flight-ranges-table__hide-on-mobile">
                    <button
                      className="sort-button"
                      onClick={() => handleSort('discontinueDate')}
                      aria-label="Sort by Discontinued Date"
                    >
                      DISCONTINUED
                      {renderSortIcon('discontinueDate')}
                    </button>
                  </th>
                  <th scope="col" className="flight-ranges-table__hide-on-small-tablet">
                    <button
                      className="sort-button"
                      onClick={() => handleSort('modDate')}
                      aria-label="Sort by Modified Date"
                    >
                      MODIFIED
                      {renderSortIcon('modDate')}
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedData.length === 0 ? (
                  <tr>
                    <td colSpan={tableColSpan} style={{ textAlign: 'center', padding: '40px' }}>
                      No flight ranges available
                    </td>
                  </tr>
                ) : (
                  sortedData.map((row: IFlightRangeData) => {
                    const isEligible = isAdmin && isAdminView && eligibleRowIds.has(row.id);
                    const isSelected = isAdmin && isAdminView && selectedRows.has(row.id);

                    return (
                      <tr
                        key={row.id}
                        className={isSelected ? 'flight-ranges-table__row--selected' : ''}
                      >
                        {isAdmin && isAdminView && (
                          <td className="flight-ranges-table__checkbox-column">
                            {isEligible && (
                              <input
                                type="checkbox"
                                className="flight-ranges-table__checkbox"
                                checked={isSelected}
                                onChange={() => handleRowCheckboxChange(row.id)}
                                aria-label={`Select flight range ${formatFlightRange(row.start, row.end)}`}
                              />
                            )}
                          </td>
                        )}
                        <td data-label="MKT. CARRIER">{row.marketingCarrier}</td>
                        <td data-label="FLIGHT RANGE">{formatFlightRange(row.start, row.end)}</td>
                        <td data-label="OP. CARRIER">{row.operatingCarrier}</td>
                        <td data-label="FLIGHT TYPE" className="flight-ranges-table__hide-on-mobile flight-ranges-table__flight-type-column">{row.flightTypeDescription}</td>
                        <td data-label="EFFECTIVE" className="effective-date">{formatDate(row.effectiveDate)}</td>
                        <td data-label="DISCONTINUED" className="discontinued-date flight-ranges-table__hide-on-mobile">{formatDate(row.discontinueDate)}</td>
                        <td data-label="MODIFIED" className="flight-ranges-table__hide-on-small-tablet">{formatDate(row.modDate)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Admin Actions - Show below table when items are selected */}
          {isAdmin && isAdminView && selectedRows.size > 0 && (
            <div className="flight-ranges-table__actions flight-ranges-table__actions--bottom">
              <div className="flight-ranges-table__selection-summary">
                {selectedRows.size} flight range{selectedRows.size === 1 ? '' : 's'} selected
              </div>
              <auro-button
                ref={(el: any) => {
                  if (el) {
                    el.addEventListener('click', handleContinue);
                  }
                }}
                aria-label={`Continue with ${selectedRows.size} selected flight range${selectedRows.size === 1 ? '' : 's'}`}
                variant="primary"
                size="lg"
                responsive
                fluid
                style={
                  {
                    '--ds-auro-button-container-image': 'var(--color-primary, #0074ca)',
                    padding: 'var(--padding-size-150, 12px) var(--padding-size-300, 24px)',
                    minWidth: '212px',
                    height: '50px'
                  } as React.CSSProperties
                }
              >
                Continue
              </auro-button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FlightRangeList;